import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { twoFactorService } from '../../services/twoFactorService';
import { useAuth } from '../../contexts/AuthContext';
import QRCode from 'qrcode';
import MethodSelection from './components/MethodSelection';
import AuthenticatorSetup from './components/AuthenticatorSetup';
import SMSSetup from './components/SMSSetup';
import BackupCodesDisplay from './components/BackupCodesDisplay';
import SetupComplete from './components/SetupComplete';

export default function TwoFactorAuthenticationSetup() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState('method-selection');
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [secret, setSecret] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentStep === 'authenticator-setup' && user) {
      generateAuthenticatorSecret();
    }
  }, [currentStep, user]);

  const generateAuthenticatorSecret = async () => {
    try {
      const newSecret = twoFactorService?.generateAuthenticatorSecret();
      setSecret(newSecret);

      const otpauthUrl = twoFactorService?.generateQRCodeURL(user?.email, newSecret);
      const qrCode = await QRCode?.toDataURL(otpauthUrl);
      setQrCodeUrl(qrCode);
    } catch (err) {
      setError('Failed to generate QR code');
    }
  };

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    if (method === 'authenticator') {
      setCurrentStep('authenticator-setup');
    } else if (method === 'sms') {
      setCurrentStep('sms-setup');
    }
  };

  const handleAuthenticatorComplete = async (deviceName, verificationCode) => {
    setLoading(true);
    setError('');
    try {
      await twoFactorService?.addAuthenticatorMethod(deviceName, secret);
      const codes = await twoFactorService?.generateBackupCodes(10);
      setBackupCodes(codes);
      setCurrentStep('backup-codes');
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSMSComplete = async (phoneNumber, deviceName) => {
    setLoading(true);
    setError('');
    try {
      await twoFactorService?.addSMSMethod(phoneNumber, deviceName);
      const codes = await twoFactorService?.generateBackupCodes(10);
      setBackupCodes(codes);
      setCurrentStep('backup-codes');
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCodesAcknowledged = () => {
    setCurrentStep('complete');
  };

  const handleFinish = () => {
    navigate('/two-factor-authentication-management');
  };

  const getStepNumber = () => {
    const steps = {
      'method-selection': 1,
      'authenticator-setup': 2,
      'sms-setup': 2,
      'backup-codes': 3,
      'complete': 4
    };
    return steps?.[currentStep] || 1;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Two-Factor Authentication Setup</h1>
          <p className="text-lg text-gray-600">Secure your account with an additional layer of protection</p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {[1, 2, 3, 4]?.map((step) => (
              <React.Fragment key={step}>
                <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                  getStepNumber() >= step ? 'bg-indigo-600 text-white' : 'bg-gray-300 text-gray-600'
                } font-semibold`}>
                  {step}
                </div>
                {step < 4 && (
                  <div className={`w-16 h-1 ${
                    getStepNumber() > step ? 'bg-indigo-600' : 'bg-gray-300'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-600">
            <span>Select Method</span>
            <span>Configure</span>
            <span>Backup Codes</span>
            <span>Complete</span>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {currentStep === 'method-selection' && (
            <MethodSelection onMethodSelect={handleMethodSelect} />
          )}

          {currentStep === 'authenticator-setup' && (
            <AuthenticatorSetup
              qrCodeUrl={qrCodeUrl}
              secret={secret}
              loading={loading}
              onComplete={handleAuthenticatorComplete}
              onBack={() => setCurrentStep('method-selection')}
            />
          )}

          {currentStep === 'sms-setup' && (
            <SMSSetup
              loading={loading}
              onComplete={handleSMSComplete}
              onBack={() => setCurrentStep('method-selection')}
            />
          )}

          {currentStep === 'backup-codes' && (
            <BackupCodesDisplay
              codes={backupCodes}
              onAcknowledge={handleBackupCodesAcknowledged}
            />
          )}

          {currentStep === 'complete' && (
            <SetupComplete
              method={selectedMethod}
              onFinish={handleFinish}
            />
          )}
        </div>

        {/* Security Information */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Why Enable Two-Factor Authentication?</h3>
          <ul className="space-y-2 text-blue-800">
            <li className="flex items-start">
              <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Adds an extra layer of security beyond your password</span>
            </li>
            <li className="flex items-start">
              <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Protects against unauthorized access even if your password is compromised</span>
            </li>
            <li className="flex items-start">
              <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Backup codes ensure you can always access your account</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}